import React from 'react';
import { CountdownTimer } from './CountdownTimer';
import { useT } from '../i18n';
interface SlideFourProps {
  onSelectCTA: (option: string) => void;
  onBack: () => void;
  selectedOption: number | null;
  elapsedSeconds: number;
}
const OPTION_HEADLINES: Record<number, string> = {
  0: 'cta.headline0',
  1: 'cta.headline1',
  2: 'cta.headline2'
};
export function SlideFour({
  onSelectCTA,
  onBack,
  selectedOption,
  elapsedSeconds
}: SlideFourProps) {
  const {
    t
  } = useT();
  const headlineKey = selectedOption !== null && OPTION_HEADLINES[selectedOption] || 'cta.headlineDefault';
  const keptPromise = elapsedSeconds <= 18;
  return <div className="flex flex-col h-full w-full justify-center animate-fade-in-up" data-id="element-190">
      {/* Back button */}
      <button onClick={onBack} className="self-start mb-4 flex items-center text-solventum-secondary hover:text-white transition-colors duration-200 text-sm font-body group" aria-label="Go back" data-id="element-191">
        <svg className="w-4 h-4 mr-1.5 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-192">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" data-id="element-193" />
        </svg>
        {t('back')}
      </button>

      {/* Stopped timer + message */}
      <div className="flex flex-col items-center mb-8" data-id="element-194">
        <CountdownTimer elapsed={elapsedSeconds} stopped data-id="element-195" />
        <div className="mt-3 text-center" data-id="element-196">
          {keptPromise ? <p className="text-solventum-primary font-headline font-bold text-base" data-id="element-197">
              {t('cta.promiseKept', {
            seconds: elapsedSeconds
          })}
            </p> : <p className="text-solventum-secondary font-body text-sm" data-id="element-198">
              {t('cta.promiseBroken')}
            </p>}
        </div>
      </div>

      <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 text-center leading-tight" data-id="element-199">
        {t(headlineKey)}
      </h2>
      <p className="text-solventum-secondary font-body text-center mb-8" data-id="element-200">
        {t('cta.subtitle')}
      </p>

      <div className="flex flex-col space-y-4 w-full" data-id="element-201">
        {/* Option A: Web meeting */}
        <button onClick={() => onSelectCTA('web-meeting')} className="w-full bg-white/5 border-2 border-solventum-secondary hover:border-solventum-primary rounded-2xl p-6 text-left transition-all duration-300 hover:bg-white/10 group" data-id="element-202">
          <div className="flex items-start gap-4" data-id="element-203">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-solventum-primary/10 border border-solventum-primary/30 flex items-center justify-center" data-id="element-204">
              <svg className="w-6 h-6 text-solventum-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-205">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" data-id="element-206" />
              </svg>
            </div>
            <div className="flex-1" data-id="element-207">
              <h3 className="text-white font-headline font-bold text-lg mb-1 group-hover:text-solventum-primary transition-colors" data-id="element-208">
                {t('cta.webMeetingTitle')}
              </h3>
              <p className="text-white/60 font-body text-sm" data-id="element-209">
                {t('cta.webMeetingDesc')}
              </p>
            </div>
            <svg className="w-5 h-5 text-solventum-secondary group-hover:text-solventum-primary transition-all duration-200 group-hover:translate-x-0.5 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-210">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-211" />
            </svg>
          </div>
        </button>

        {/* Option B: Send material */}
        <button onClick={() => onSelectCTA('send-material')} className="w-full bg-white/5 border-2 border-solventum-secondary hover:border-solventum-primary rounded-2xl p-6 text-left transition-all duration-300 hover:bg-white/10 group" data-id="element-212">
          <div className="flex items-start gap-4" data-id="element-213">
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-solventum-primary/10 border border-solventum-primary/30 flex items-center justify-center" data-id="element-214">
              <svg className="w-6 h-6 text-solventum-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} data-id="element-215">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" data-id="element-216" />
              </svg>
            </div>
            <div className="flex-1" data-id="element-217">
              <h3 className="text-white font-headline font-bold text-lg mb-1 group-hover:text-solventum-primary transition-colors" data-id="element-218">
                {t('cta.materialTitle')}
              </h3>
              <p className="text-white/60 font-body text-sm" data-id="element-219">
                {t('cta.materialDesc')}
              </p>
            </div>
            <svg className="w-5 h-5 text-solventum-secondary group-hover:text-solventum-primary transition-all duration-200 group-hover:translate-x-0.5 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-220">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" data-id="element-221" />
            </svg>
          </div>
        </button>
      </div>
    </div>;
}